import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { EmpresaItem, GrupoEmpresa } from '../models/grupos-empresa.models';
import { GruposEmpresaService } from '../services/grupos-empresa.service';
import { ConfirmDialogModel } from 'app/modules/common/components/confirmation-dialog/confirm-dialog-model';
import { ConfirmationDialogComponent } from 'app/modules/common/components/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-grupos-empresa-details',
    templateUrl: './grupos-empresa-details.component.html',
    styleUrls: ['./grupos-empresa-details.component.scss'],
    standalone: false
})
export class GruposEmpresaDetailsComponent implements OnChanges {
    @Input() grupo: GrupoEmpresa;
    @Input() empresasDisponibles: EmpresaItem[] = [];
    @Input() modo: 'ver' | 'crear' | 'editar' = 'ver';

    public form: FormGroup;
    public mostrarPanelAnadirEmpresa = false;

    public empresasSeleccionadasCrear: EmpresaItem[] = [];
    public empresasSeleccionadasAnadir: EmpresaItem[] = [];

    public busquedaCrear = '';
    public busquedaAnadir = '';

    private grupoBackup: GrupoEmpresa | null = null;

    constructor(
        private fb: FormBuilder,
        private gruposEmpresaService: GruposEmpresaService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) {
        this.form = this.fb.group({
            nombre: ['', [Validators.required, Validators.maxLength(200)]],
            empresaIds: [[], Validators.required],
            empresaIdsNuevas: [[]]
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['modo'] || changes['grupo'] || changes['empresasDisponibles']) {
            this.prepararVista();
        }
    }

    prepararVista(): void {
        this.mostrarPanelAnadirEmpresa = false;
        this.empresasSeleccionadasCrear = [];
        this.empresasSeleccionadasAnadir = [];
        this.busquedaCrear = '';
        this.busquedaAnadir = '';

        if (this.modo === 'crear') {
            this.grupoBackup = null;

            this.form.reset({
                nombre: '',
                empresaIds: [],
                empresaIdsNuevas: []
            });

            this.form.get('empresaIds')?.setValidators([Validators.required]);
            this.form.get('empresaIds')?.updateValueAndValidity();
            return;
        }

        this.form.get('empresaIds')?.clearValidators();
        this.form.get('empresaIds')?.updateValueAndValidity();

        if (this.grupo) {
            this.grupoBackup = JSON.parse(JSON.stringify(this.grupo));
            this.form.patchValue({
                nombre: this.grupo.nombre,
                empresaIds: [],
                empresaIdsNuevas: []
            });
        } else {
            this.form.reset({
                nombre: '',
                empresaIds: [],
                empresaIdsNuevas: []
            });
        }
    }

    get empresasDisponiblesParaCrear(): EmpresaItem[] {
        return (this.empresasDisponibles || [])
            .filter(e => !!e?.nombre)
            .filter(e => !this.empresasSeleccionadasCrear.some(s => s.empresaId === e.empresaId))
            .sort((a, b) => (a?.nombre || '').localeCompare(b?.nombre || ''));
    }

    get empresasFiltradasCrear(): EmpresaItem[] {
        const texto = (this.busquedaCrear || '').trim().toLowerCase();

        if (!texto) {
            return this.empresasDisponiblesParaCrear;
        }

        return this.empresasDisponiblesParaCrear.filter(e =>
            (e.nombre || '').toLowerCase().includes(texto)
        );
    }

    get empresasDisponiblesParaAnadir(): EmpresaItem[] {
        if (!this.grupo || !this.empresasDisponibles?.length) {
            return [];
        }

        const idsAsignadas = (this.grupo.empresas || []).map(e => e.empresaId);

        return this.empresasDisponibles
            .filter(e => !!e?.nombre)
            .filter(e => !idsAsignadas.includes(e.empresaId))
            .filter(e => !this.empresasSeleccionadasAnadir.some(s => s.empresaId === e.empresaId))
            .sort((a, b) => (a?.nombre || '').localeCompare(b?.nombre || ''));
    }

    get empresasFiltradasAnadir(): EmpresaItem[] {
        const texto = (this.busquedaAnadir || '').trim().toLowerCase();

        if (!texto) {
            return this.empresasDisponiblesParaAnadir;
        }

        return this.empresasDisponiblesParaAnadir.filter(e =>
            (e.nombre || '').toLowerCase().includes(texto)
        );
    }

    activarEdicion(): void {
        if (!this.grupo) {
            return;
        }

        this.modo = 'editar';
        this.form.patchValue({
            nombre: this.grupo.nombre,
            empresaIdsNuevas: []
        });

        this.empresasSeleccionadasAnadir = [];
        this.busquedaAnadir = '';
    }

    cancelar(): void {
        this.mostrarPanelAnadirEmpresa = false;
        this.empresasSeleccionadasCrear = [];
        this.empresasSeleccionadasAnadir = [];
        this.busquedaCrear = '';
        this.busquedaAnadir = '';

        this.form.patchValue({
            empresaIdsNuevas: [],
            empresaIds: []
        });

        if (this.modo === 'crear') {
            this.form.reset({
                nombre: '',
                empresaIds: [],
                empresaIdsNuevas: []
            });
            return;
        }

        if (this.grupoBackup) {
            this.grupo = JSON.parse(JSON.stringify(this.grupoBackup));
            this.form.patchValue({
                nombre: this.grupo.nombre,
                empresaIds: [],
                empresaIdsNuevas: []
            });
        }

        this.modo = 'ver';
    }

    guardar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.form.get('empresaIds')?.markAsTouched();
            this.form.get('empresaIds')?.updateValueAndValidity();
            return;
        }

        if (this.modo === 'crear') {
            this.crearGrupo();
            return;
        }

        if (this.modo === 'editar') {
            this.guardarEdicion();
        }
    }

    private crearGrupo(): void {
        this.gruposEmpresaService.crearGrupo({
            nombre: this.form.value.nombre,
            empresaIds: this.form.value.empresaIds
        }).subscribe(
            (response: any) => {
                if (response && response.result) {
                    this.snackBar.open('Grupo creado correctamente', 'Cerrar', { duration: 2500 });
                    this.modo = 'ver';
                    this.empresasSeleccionadasCrear = [];
                    this.busquedaCrear = '';
                    this.gruposEmpresaService.recargarGrupos$.next();
                } else {
                    this.snackBar.open('No se pudo crear el grupo', 'Cerrar', { duration: 3000 });
                }
            },
            error => {
                console.error('Error al crear grupo', error);
                this.snackBar.open('Error al crear el grupo', 'Cerrar', { duration: 3000 });
            }
        );
    }

    private guardarEdicion(): void {
        if (!this.grupo) {
            return;
        }

        this.gruposEmpresaService.editarGrupo({
            grupoId: this.grupo.grupoId,
            nombre: this.form.value.nombre
        }).subscribe(
            (response: any) => {
                if (response && response.result) {
                    this.grupo.nombre = this.form.value.nombre;
                    this.grupoBackup = JSON.parse(JSON.stringify(this.grupo));
                    this.modo = 'ver';
                    this.snackBar.open('Grupo editado correctamente', 'Cerrar', { duration: 2500 });
                    this.gruposEmpresaService.recargarGrupos$.next();
                } else {
                    this.snackBar.open('No se pudo editar el grupo', 'Cerrar', { duration: 3000 });
                }
            },
            error => {
                console.error('Error al editar grupo', error);
                this.snackBar.open('Error al editar el grupo', 'Cerrar', { duration: 3000 });
            }
        );
    }

    eliminarGrupo(): void {
        if (!this.grupo) {
            return;
        }

        const dialogData = new ConfirmDialogModel(
            '¿Eliminar grupo?',
            `Se va a eliminar el grupo "${this.grupo.nombre}".`
        );

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: dialogData
        });

        dialogRef.afterClosed().subscribe((response) => {
            if (!response) {
                return;
            }

            this.gruposEmpresaService.eliminarGrupo(this.grupo.grupoId).subscribe(
                (result: any) => {
                    if (result && result.result) {
                        this.snackBar.open('Grupo eliminado correctamente', 'Cerrar', { duration: 2500 });
                        this.gruposEmpresaService.recargarGrupos$.next();
                    } else {
                        this.snackBar.open('No se pudo eliminar el grupo', 'Cerrar', { duration: 3000 });
                    }
                },
                error => {
                    console.error('Error al eliminar grupo', error);
                    this.snackBar.open('Error al eliminar el grupo', 'Cerrar', { duration: 3000 });
                }
            );
        });
    }

    anadirEmpresa(): void {
        if (!this.grupo) {
            return;
        }

        this.mostrarPanelAnadirEmpresa = true;
        this.empresasSeleccionadasAnadir = [];
        this.busquedaAnadir = '';
        this.form.patchValue({ empresaIdsNuevas: [] });
    }

    cancelarAnadirEmpresa(): void {
        this.mostrarPanelAnadirEmpresa = false;
        this.empresasSeleccionadasAnadir = [];
        this.busquedaAnadir = '';
        this.form.patchValue({ empresaIdsNuevas: [] });
    }

    guardarNuevaEmpresa(): void {
        if (!this.grupo) {
            return;
        }

        const empresaIdsNuevas: number[] = this.form.get('empresaIdsNuevas')?.value || [];

        if (!empresaIdsNuevas.length) {
            return;
        }

        const peticiones = empresaIdsNuevas.map(empresaId =>
            this.gruposEmpresaService.anadirEmpresa({
                grupoId: this.grupo.grupoId,
                empresaId
            })
        );

        forkJoin(peticiones).subscribe(
            (responses: any[]) => {
                const todasOk = responses.every(r => r && r.result);

                if (todasOk) {
                    empresaIdsNuevas.forEach(empresaId => {
                        const empresaSeleccionada = this.empresasDisponibles.find(x => x.empresaId === empresaId);

                        if (empresaSeleccionada) {
                            const yaExiste = this.grupo.empresas.some(x => x.empresaId === empresaSeleccionada.empresaId);

                            if (!yaExiste) {
                                this.grupo.empresas.push({
                                    empresaId: empresaSeleccionada.empresaId,
                                    nombre: empresaSeleccionada.nombre
                                } as EmpresaItem);
                            }
                        }
                    });

                    this.grupo.empresas = [...this.grupo.empresas].sort((a, b) =>
                        (a?.nombre || '').localeCompare(b?.nombre || '')
                    );

                    this.mostrarPanelAnadirEmpresa = false;
                    this.empresasSeleccionadasAnadir = [];
                    this.busquedaAnadir = '';
                    this.form.patchValue({ empresaIdsNuevas: [] });

                    this.snackBar.open('Empresas añadidas correctamente', 'Cerrar', { duration: 2500 });
                } else {
                    this.snackBar.open('No se pudieron añadir todas las empresas', 'Cerrar', { duration: 3000 });
                }
            },
            error => {
                console.error('Error al añadir empresas', error);
                this.snackBar.open('Error al añadir las empresas', 'Cerrar', { duration: 3000 });
            }
        );
    }

    confirmarQuitarEmpresa(empresa: EmpresaItem, index: number): void {
        if (!this.grupo || !empresa) {
            return;
        }

        const dialogData = new ConfirmDialogModel(
            '¿Eliminar empresa del grupo?',
            `Se va a eliminar "${empresa.nombre}" del grupo.`
        );

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: dialogData
        });

        dialogRef.afterClosed().subscribe((response) => {
            if (response) {
                this.quitarEmpresa(empresa, index);
            }
        });
    }

    quitarEmpresa(empresa: EmpresaItem, index: number): void {
        if (!this.grupo || !empresa) {
            return;
        }

        this.gruposEmpresaService.quitarEmpresa({
            grupoId: this.grupo.grupoId,
            empresaId: empresa.empresaId
        }).subscribe(
            (response: any) => {
                if (response && response.result) {
                    this.grupo.empresas.splice(index, 1);
                    this.grupo.empresas = [...this.grupo.empresas];
                    this.snackBar.open('Empresa eliminada del grupo', 'Cerrar', { duration: 2500 });
                } else {
                    this.snackBar.open('No se pudo quitar la empresa', 'Cerrar', { duration: 3000 });
                }
            },
            error => {
                console.error('Error al quitar empresa', error);
                this.snackBar.open('Error al quitar la empresa', 'Cerrar', { duration: 3000 });
            }
        );
    }

    seleccionarEmpresaCrear(empresa: EmpresaItem): void {
        if (!empresa?.empresaId) {
            return;
        }

        const yaExiste = this.empresasSeleccionadasCrear.some(e => e.empresaId === empresa.empresaId);
        if (yaExiste) {
            return;
        }

        this.empresasSeleccionadasCrear.push({
            empresaId: empresa.empresaId,
            nombre: empresa.nombre
        });

        this.empresasSeleccionadasCrear = [...this.empresasSeleccionadasCrear].sort((a, b) =>
            (a?.nombre || '').localeCompare(b?.nombre || '')
        );

        this.form.patchValue({
            empresaIds: this.empresasSeleccionadasCrear.map(e => e.empresaId)
        });

        this.form.get('empresaIds')?.markAsTouched();
        this.form.get('empresaIds')?.updateValueAndValidity();
        this.busquedaCrear = '';
    }

    quitarEmpresaSeleccionadaCrear(index: number): void {
        this.empresasSeleccionadasCrear.splice(index, 1);
        this.empresasSeleccionadasCrear = [...this.empresasSeleccionadasCrear];

        this.form.patchValue({
            empresaIds: this.empresasSeleccionadasCrear.map(e => e.empresaId)
        });

        this.form.get('empresaIds')?.markAsTouched();
        this.form.get('empresaIds')?.updateValueAndValidity();
    }

    seleccionarEmpresaAnadir(empresa: EmpresaItem): void {
        if (!empresa?.empresaId || !this.grupo) {
            return;
        }

        const yaEnGrupo = this.grupo.empresas?.some(e => e.empresaId === empresa.empresaId);
        if (yaEnGrupo) {
            return;
        }

        const yaSeleccionada = this.empresasSeleccionadasAnadir.some(e => e.empresaId === empresa.empresaId);
        if (yaSeleccionada) {
            return;
        }

        this.empresasSeleccionadasAnadir.push({
            empresaId: empresa.empresaId,
            nombre: empresa.nombre
        });

        this.empresasSeleccionadasAnadir = [...this.empresasSeleccionadasAnadir].sort((a, b) =>
            (a?.nombre || '').localeCompare(b?.nombre || '')
        );

        this.form.patchValue({
            empresaIdsNuevas: this.empresasSeleccionadasAnadir.map(e => e.empresaId)
        });

        this.busquedaAnadir = '';
    }

    quitarEmpresaSeleccionadaAnadir(index: number): void {
        this.empresasSeleccionadasAnadir.splice(index, 1);
        this.empresasSeleccionadasAnadir = [...this.empresasSeleccionadasAnadir];

        this.form.patchValue({
            empresaIdsNuevas: this.empresasSeleccionadasAnadir.map(e => e.empresaId)
        });
    }

    seleccionarEmpresaCrearDesdeArbol(node: any): void {
        if (!node?.id || !node?.text) {
            return;
        }

        this.seleccionarEmpresaCrear({
            empresaId: Number(node.id),
            nombre: node.text
        });
    }

    seleccionarEmpresaAnadirDesdeArbol(node: any): void {
        if (!node?.id || !node?.text) {
            return;
        }

        this.seleccionarEmpresaAnadir({
            empresaId: Number(node.id),
            nombre: node.text
        });
    }
}